import type { IEnterpriseSalesInquiryFormState } from "types/enterprise/enterprise-sales-inquiry-form.types";
import {
  EnterpriseSalesConcurrencyEnum,
  EnterpriseSalesInterestEnum,
  EnterpriseSalesSeatRangeEnum,
  EnterpriseSalesTestCaseScaleEnum,
  EnterpriseSalesTimelineEnum,
} from "types/enterprise/enterprise-sales-inquiry.enums";

/** Estimated-seat ranges, in select order. Labels: `publicPages:enterpriseSalesInquiry.seatRange.<value>`. */
export const ENTERPRISE_SALES_SEAT_RANGES: readonly EnterpriseSalesSeatRangeEnum[] = [
  EnterpriseSalesSeatRangeEnum.UNSPECIFIED,
  EnterpriseSalesSeatRangeEnum.FOUR_TO_TEN,
  EnterpriseSalesSeatRangeEnum.ELEVEN_TO_TWENTYFIVE,
  EnterpriseSalesSeatRangeEnum.TWENTYSIX_TO_HUNDRED,
  EnterpriseSalesSeatRangeEnum.OVER_HUNDRED,
];

/** Concurrent-workflow levels, in select order. Labels: `publicPages:enterpriseSalesInquiry.concurrency.<value>`. */
export const ENTERPRISE_SALES_CONCURRENCY_LEVELS: readonly EnterpriseSalesConcurrencyEnum[] = [
  EnterpriseSalesConcurrencyEnum.UNSPECIFIED,
  EnterpriseSalesConcurrencyEnum.TWENTY_TO_FIFTY,
  EnterpriseSalesConcurrencyEnum.FIFTY_TO_HUNDRED,
  EnterpriseSalesConcurrencyEnum.HUNDRED_PLUS,
];

/** Expected test-case volumes, in select order. Labels: `publicPages:enterpriseSalesInquiry.testCaseScale.<value>`. */
export const ENTERPRISE_SALES_TEST_CASE_SCALES: readonly EnterpriseSalesTestCaseScaleEnum[] = [
  EnterpriseSalesTestCaseScaleEnum.UNSPECIFIED,
  EnterpriseSalesTestCaseScaleEnum.UNDER_ONE_HUNDRED,
  EnterpriseSalesTestCaseScaleEnum.ONE_HUNDRED_TO_FIVE_HUNDRED,
  EnterpriseSalesTestCaseScaleEnum.FIVE_HUNDRED_TO_TWO_THOUSAND,
  EnterpriseSalesTestCaseScaleEnum.TWO_THOUSAND_TO_TEN_THOUSAND,
  EnterpriseSalesTestCaseScaleEnum.TEN_THOUSAND_PLUS,
];

/** Purchase timelines, in select order. Labels: `publicPages:enterpriseSalesInquiry.timeline.<value>`. */
export const ENTERPRISE_SALES_TIMELINES: readonly EnterpriseSalesTimelineEnum[] = [
  EnterpriseSalesTimelineEnum.UNSPECIFIED,
  EnterpriseSalesTimelineEnum.ASAP,
  EnterpriseSalesTimelineEnum.ONE_TO_THREE_MONTHS,
  EnterpriseSalesTimelineEnum.EXPLORING,
];

/** Enterprise customization interests, in checkbox order. Labels: `publicPages:enterpriseSalesInquiry.interest.<value>`. */
export const ENTERPRISE_SALES_INTERESTS: readonly EnterpriseSalesInterestEnum[] = [
  EnterpriseSalesInterestEnum.ADMIN_SUB_ACCOUNTS,
  EnterpriseSalesInterestEnum.SHARED_PROJECTS,
  EnterpriseSalesInterestEnum.PAY_AS_YOU_GO,
  EnterpriseSalesInterestEnum.SSO_SECURITY,
  EnterpriseSalesInterestEnum.DEDICATED_SUPPORT,
];

/** Values embedded in the generated sales email (lead source). */
export const ENTERPRISE_SALES_SUBMISSION_SOURCE = {
  BILLING_PLAN_SELECTION: "Billing — Choose your plan",
  MARKETING_PRICING: "Marketing — Pricing",
  ORGANIZATION_CAPACITY: "Organization — Capacity above the ceiling",
} as const;

/**
 * Initial form state for a new enterprise inquiry.
 *
 * @returns Default inquiry form values
 */
export function createDefaultEnterpriseSalesInquiryForm(): IEnterpriseSalesInquiryFormState {
  return {
    contactName: "",
    workEmail: "",
    companyName: "",
    estimatedSeats: EnterpriseSalesSeatRangeEnum.UNSPECIFIED,
    concurrentWorkflows: EnterpriseSalesConcurrencyEnum.UNSPECIFIED,
    testCaseScale: EnterpriseSalesTestCaseScaleEnum.UNSPECIFIED,
    timeline: EnterpriseSalesTimelineEnum.UNSPECIFIED,
    interests: {
      [EnterpriseSalesInterestEnum.ADMIN_SUB_ACCOUNTS]: false,
      [EnterpriseSalesInterestEnum.SHARED_PROJECTS]: false,
      [EnterpriseSalesInterestEnum.PAY_AS_YOU_GO]: false,
      [EnterpriseSalesInterestEnum.SSO_SECURITY]: false,
      [EnterpriseSalesInterestEnum.DEDICATED_SUPPORT]: false,
    },
    comments: "",
  };
}
